import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {PaletteColorsEnum} from '../../../../libs/shared/core/src/models/api/paletteModel';
import {ColorTeammateAvatar} from '../../../../libs/shared/ui/src/components/avatar/colorTeammateAvatar';
import {PlaceholderTeammateAvatar} from '../../../../libs/shared/ui/src/components/avatar/placeholderTeammateAvatar';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';

/*
 * Props.
 */

interface OnboardingInboxInviteTeammatesVisualProps {
  invitedTeammateHandles: ReadonlyArray<string>;
}

/*
 * Style.
 */

const StyledContainerDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: 240px;
  min-height: 72px;
  gap: 16px;
  border-radius: 13px;
  box-shadow: 0px 0px 10px 0px rgba(0, 27, 56, 0.1);
  background: ${(p) => p.theme.greys.white};
  padding: 16px;
`;

const StyledTeammateDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 40px;
  gap: 12px;
`;

const StyledTeammateTextDiv = styled.div`
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.medium};
  line-height: 16px;
  ${ellipsis()};
`;

const StyledNoTeammatesTextDiv = styled(StyledTeammateTextDiv)`
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledAdditionalTeammatesTextDiv = styled.div`
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.medium};
  line-height: 16px;
  color: ${(p) => p.theme.greys.shade60};
  display: flex;
  align-items: center;
  justify-content: center;
`;

/*
 * Component.
 */

export const OnboardingInboxInviteTeammatesVisual: FC<OnboardingInboxInviteTeammatesVisualProps> = (
  props,
) => {
  const {invitedTeammateHandles} = props;

  if (invitedTeammateHandles.length === 0) {
    return (
      <StyledContainerDiv>
        <StyledTeammateDiv>
          <PlaceholderTeammateAvatar />
          <StyledNoTeammatesTextDiv>
            <Trans id="onboarding-inbox-invite-teammates-visual-no-teammates">No teammates yet</Trans>
          </StyledNoTeammatesTextDiv>
        </StyledTeammateDiv>
      </StyledContainerDiv>
    );
  }

  const visibleTeammatesLimit = 3;

  return (
    <StyledContainerDiv>
      {invitedTeammateHandles.slice(0, visibleTeammatesLimit).map((handle, i) => (
        <StyledTeammateDiv>
          <ColorTeammateAvatar
            initials={handle[0]}
            size={40}
            color={
              [PaletteColorsEnum.ORANGE, PaletteColorsEnum.GREEN, PaletteColorsEnum.YELLOW][
                i % visibleTeammatesLimit
              ]
            }
          />
          <StyledTeammateTextDiv>{handle}</StyledTeammateTextDiv>
        </StyledTeammateDiv>
      ))}
      {invitedTeammateHandles.length > 3 && (
        <StyledAdditionalTeammatesTextDiv>
          +{invitedTeammateHandles.length - visibleTeammatesLimit}{' '}
          <Trans id="onboarding-inbox-invite-teammates-visual-more-teammates">more</Trans>
        </StyledAdditionalTeammatesTextDiv>
      )}
    </StyledContainerDiv>
  );
};
